import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { FormSection } from '@ksyos/design-system';
import type { BariPreSecretarialAssistantMedScreeningReportData } from '@common/validation/customControls/bariPre/bariPreSecretarialAssistantMedScreeningReport/types';
import { BmiFormGroup } from '@frontend/components/pages/episodeCategory/customControls/BmiProcessActivityFormGroupFields';
import {
    BooleanRadioGroupFormGroup,
    DateInputFormGroup,
    NumberInputColumnRowFormGroup,
    TextAreaFormGroup,
} from '@frontend/components/pages/episodeCategory/customControls/processActivityFormGroupFields';
import { YesNoWithConditionalTextAreaFormGroup } from '@frontend/components/pages/episodeCategory/customControls/YesNoWithConditionalTextAreaFormGroup';
import {
    useProcessActivityContext,
    useProcessActivityEditContext,
} from '@frontend/utils/hooks/useProcessActivityContext';
import { BariatricsRokenEdit } from '../../../bariatrics/BariatricsRokenEdit';
import { BariPreSecretarialAssistentMedScreeningReportEditTabak } from './v2/BariPreSecretarialAssistantMedScreeningReportEditTabak';

export function BariPreSecretarialAssistentMedScreeningReportEdit(): JSX.Element {
    const { customControlVersion } = useProcessActivityContext();
    const { data, errors, onChange } =
        useProcessActivityEditContext<BariPreSecretarialAssistantMedScreeningReportData>();
    /**
     * @deprecated CS-20926: Clean up when version 1 is no longer in progress on production.
     * This means removing version 1 code and conditionals, and use the EditTabak component directly.
     */
    const shouldShowRoken = customControlVersion === 1 && 'roken' in data;
    const shouldShowTabak = customControlVersion === 2 && 'tabak_gebruik' in data;

    return (
        <>
            <FormSection title="Biometrische gegevens">
                <DateInputFormGroup
                    error={getDatumScreeningError()}
                    fieldKey="datum_screening"
                    isRequired={true}
                    label="Datum screening"
                    maxDate={DateTime.now().format()}
                />
                <BmiFormGroup
                    errors={errors}
                    fieldKey={{ height: 'lengte_in_cm', weight: 'gewicht_in_kg' }}
                    height={data.lengte_in_cm}
                    isRequired={true}
                    weight={data.gewicht_in_kg}
                />
                <NumberInputColumnRowFormGroup
                    appendedLabel="kg"
                    fieldKey="hoogste_gewicht_in_kg"
                    hasErrors={errors.has('hoogste_gewicht_in_kg')}
                    isRequired={true}
                    label="Hoogste lichaamsgewicht"
                    maximumFractionDigits={2}
                />
                <NumberInputColumnRowFormGroup
                    appendedLabel="cm"
                    fieldKey="buikomvang"
                    hasErrors={errors.has('buikomvang')}
                    isRequired={true}
                    label="Buikomvang"
                />
                <BooleanRadioGroupFormGroup
                    fieldKey="bloeddruk_gemeten"
                    isRequired={true}
                    label="Bloeddruk gemeten"
                    onChange={handleChangeBloeddrukGemeten}
                    type="radio"
                />
                {data.bloeddruk_gemeten && (
                    <>
                        <NumberInputColumnRowFormGroup
                            appendedLabel="mmHg"
                            fieldKey="bloeddruk_systolisch"
                            hasErrors={errors.has('bloeddruk_systolisch')}
                            isRequired={true}
                            label="Bloeddruk systolisch"
                        />
                        <NumberInputColumnRowFormGroup
                            appendedLabel="mmHg"
                            fieldKey="bloeddruk_diastolisch"
                            hasErrors={errors.has('bloeddruk_diastolisch')}
                            isRequired={true}
                            label="Bloeddruk diastolisch"
                        />
                    </>
                )}
                <NumberInputColumnRowFormGroup
                    appendedLabel="/min"
                    fieldKey="pols"
                    hasErrors={errors.has('pols')}
                    isRequired={true}
                    label="Pols"
                />
                <NumberInputColumnRowFormGroup
                    appendedLabel="%"
                    fieldKey="saturatie"
                    hasErrors={errors.has('saturatie')}
                    label="Saturatie"
                />
            </FormSection>

            <FormSection title="Intoxicaties">
                <BooleanRadioGroupFormGroup
                    fieldKey="alcohol"
                    isClearable={true}
                    label="Alcohol"
                    onChange={handleChangeAlcohol}
                    type="radio"
                />
                {data.alcohol && (
                    <>
                        <NumberInputColumnRowFormGroup
                            appendedLabel="per week"
                            fieldKey="alcohol_aantal_eenheden_per_week"
                            label="Aantal eenheden"
                            maximumFractionDigits={2}
                            tooltipText="Elk glas staat gelijk aan 1 eenheid. Een standaardglas alcoholhoudende drank bevat rond de 10 gram alcohol (ca. 12 ml). Dit zit ongeveer in 250 ml bier, 100 ml wijn en 35 ml sterke drank."
                        />
                        <TextAreaFormGroup
                            fieldKey="alcohol_toelichting"
                            label="Toelichting alcohol"
                        />
                    </>
                )}
                {shouldShowRoken && (
                    <BariatricsRokenEdit
                        data={data}
                        onChange={onChange}
                    />
                )}
                {shouldShowTabak && <BariPreSecretarialAssistentMedScreeningReportEditTabak />}
                <YesNoWithConditionalTextAreaFormGroup
                    fieldKey="drugs"
                    fieldValue={data.drugs}
                    isClearable={true}
                    label="Drugs"
                    textAreaFieldKey="drugs_toelichting"
                    textAreaLabel="Toelichting drugs"
                />
            </FormSection>
        </>
    );

    // Todo CS-20031: fix in Koi
    function getDatumScreeningError() {
        if (errors.has('datum_screening')) {
            if (!data.datum_screening) {
                return 'any_required';
            }

            return errors.get('datum_screening');
        }
    }

    async function handleChangeBloeddrukGemeten(value: boolean | null): Promise<void> {
        return onChange(
            {
                bloeddruk_diastolisch: null,
                bloeddruk_gemeten: value,
                bloeddruk_systolisch: null,
            },
            'bloeddruk_gemeten'
        );
    }

    async function handleChangeAlcohol(value: boolean | null): Promise<void> {
        return onChange(
            {
                alcohol: value,
                alcohol_aantal_eenheden_per_week: null,
                alcohol_toelichting: null,
            },
            'alcohol'
        );
    }
}
